import { useCallback, useContext, useEffect, useState } from 'react';
import type {
  GridApi,
  GridReadyEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import _ from 'lodash';

import aguid from 'lib/aguid';

import buildGridOptions from '../../Grid/buildGridOptions';
import useChartDrillDowns from '../../../hooks/useChartDrillDowns';
import useColDefs from './useColDefs';
import useRowData from './useRowData';
import useSingleMetricDateMatrixData from './useSingleMetricDateMatrixData';
import useRowHighlighter from '../../../hooks/useRowHighlighter';
import useWindowSize from '../../../hooks/useWindowSize';
import useIsCompatibleComparison from '../../../hooks/useIsCompatableComparison';
import { RowHeightContext } from '../../../contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';

const useSingleMetricDateMatrixContent = ({
  gadget,
  gridApi,
  setGridApi,
}: {
  gadget: VisualisationDefinitions.SingleMetricDateMatrix;
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
}) => {
  // Context
  const isCompatibleComparison = useIsCompatibleComparison(gadget);
  const { rowHeight } = useContext(RowHeightContext);

  // State
  const [visId] = useState<string>(() => aguid());
  const [isFlashVisible, setIsFlashVisible] = useState<boolean>(false);

  // Hooks
  const {
    response,
    onShowAllPressed,
    isLoading,
    isInitialLoading,
    isLoadingAll,
    totalItems,
    currentItems,
    orderedBy,
    isAllDataInitiallyAvailable,
  } = useSingleMetricDateMatrixData(gadget);
  const { colDefs } = useColDefs(
    response,
    gadget,
    !!gadget.isSizedToFitOverridden,
  );
  useRowHighlighter({
    gridApi,
    drillField: gadget.groupByField,
    visId,
  });
  const { isMobile } = useWindowSize();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dismissFlash = useCallback(
    _.debounce(() => {
      setIsFlashVisible(false);
    }, 10000),
    [],
  );

  // After a sort: say it only sorted what's loaded, if that's not everything
  const onRowsSorted = useCallback(
    (displayedRowCount: number) => {
      // We can find more terms than totalTerms in the case
      // where the terms includes a "Blank" term
      if (isAllDataInitiallyAvailable || displayedRowCount >= totalItems) {
        return;
      }

      setIsFlashVisible(true);
      dismissFlash();
    },
    [isAllDataInitiallyAvailable, totalItems, dismissFlash],
  );
  const onColumnSort = useCallback(
    (event: SortChangedEvent) => onRowsSorted(event.api.getDisplayedRowCount()),
    [onRowsSorted],
  );

  const { rowData, totalsRowData } = useRowData({ response, colDefs, gadget });
  const { onDrillDown } = useChartDrillDowns(
    gadget.groupByField,
    undefined,
    'matrix',
  );

  const gridOptions = {
    ...buildGridOptions({
      columnDefs: colDefs,
      rowModelType: 'clientSide' as 'clientSide',
      rowHeight,
    }),
    rowData,
    context: {
      onDrillDown,
    },
    rowSelection: {
      mode: 'multiRow' as const,
      checkboxes: false,
      headerCheckbox: false,
    },
    suppressCellFocus: true,
  };

  const onGridReady = (event: GridReadyEvent) => {
    setGridApi(event.api);
  };

  useEffect(() => {
    if (gridApi && rowData && colDefs.length > 0 && response) {
      gridApi.setGridOption('rowData', rowData);
    }
  }, [colDefs.length, gridApi, response, rowData]);

  useEffect(() => {
    if (gridApi && response && response.isFullSet) {
      gridApi.setGridOption('columnDefs', colDefs);
    }
  }, [colDefs, gridApi, response]);

  useEffect(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.setGridOption('loading', isLoading);
    }
  }, [gridApi, isLoading]);

  return {
    isCompatibleComparison,
    isInitialLoading,
    isLoading,
    gridOptions,
    colDefs,
    rowData,
    totalsRowData,
    visId,
    onDrillDown,
    onGridReady,
    onColumnSort,
    onRowsSorted,
    isFlashVisible,
    onFlashDismissed: () => setIsFlashVisible(false),
    currentItems,
    autoSizeFirstColumnOnly: !!gadget.isSizedToFitOverridden && !isMobile,
    singleMetricMatrixContextValue: {
      isLoadingAll,
      isAllDataLoaded: response ? response.isFullSet : true,
      onShowAllPressed,
      currentItems,
      totalItems,
      orderedBy,
      isAllDataInitiallyAvailable,
      chartDef: gadget,
    },
  };
};

export default useSingleMetricDateMatrixContent;
